import React, { useEffect } from 'react';
import { ResponsiveValue } from 'styled-system';
import { AnimatePresence } from 'framer-motion';

import Portal from '../Portal';
import { Backdrop, Title } from './components';

import { Container } from './Modal.styles';

import { dropIn } from './motion';
import { Variant } from './types';

export interface ModalProps {
  /** Modal visibility */
  isOpen: boolean;
  /**
   * Close event handler
   */
  onClose: () => void;
  /**
   * Children nodes
   */
  children: React.ReactNode;
  /**
   * Modal variant
   */
  variant?: Variant;
  /**
   * Modal width
   */
  width?: ResponsiveValue<string | number>;
}

/**
- The Modal component works as a container that exposes its children in a dialog box.
**/
const Modal = ({
  isOpen,
  children,
  variant = 'window',
  width,
  onClose,
}: ModalProps) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <Portal>
      <AnimatePresence>
        {isOpen && (
          <Backdrop onClick={onClose}>
            <Container
              role="dialog"
              aria-modal="true"
              variants={dropIn}
              initial="hidden"
              animate="visible"
              exit="exit"
              variant={variant}
              width={width}
              onClick={(e) => e.stopPropagation()}
            >
              {children}
            </Container>
          </Backdrop>
        )}
      </AnimatePresence>
    </Portal>
  );
};

export default Object.assign(Modal, {
  Title,
});
